import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNift} from '~/common/hooks/useNift';
import {VendorLogos} from '~/common/vendors/VendorLogos';
import {
  selectSheetId,
  selectShortLink,
  selectWishlistOption,
} from '~/SignUpSheets/reducers/sheet/selectors';
import {Button} from '~library/atoms/Button';
import {Link} from '~library/atoms/Link';
import {Typography} from '~library/atoms/Typography';
import {Video} from '~library/atoms/Video';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';
import {CheckmarkIcon} from '~library/svg/CheckmarkIcon';
import {LinkIcon} from '~library/svg/LinkIcon';
import {SocialFbIcon} from '~library/svg/SocialFbIcon';
import {SocialIgIcon} from '~library/svg/SocialIgIcon';
import {SocialWaIcon} from '~library/svg/SocialWaIcon';
import {MailIcon} from '~library/svg/v2/MailIcon';

import {coin, largishAndUpQuery} from '~libSass/base/_exports.sass';
import './HostConfirm.sass';

export const HostConfirm = () => {
  const [copied, setCopied] = useState(false);
  const [showNiftModal, setShowNiftModal] = useState(true);
  const {matches: isDesktop} = useMatchQuery(`(${largishAndUpQuery})`);
  const email = useSelector((state) => state.user.email);
  const {niftGiftAvailable, niftAffiliateUrl} = useNift({email});
  const firstName = useSelector((state) => state.user.firstName);
  const sheetId = useSelector(selectSheetId);
  const shortLink = useSelector(selectShortLink);
  const wishlistOption = useSelector(selectWishlistOption);
  const hasDefaultWishlist = wishlistOption === 'evite-wishlist';

  const onCloseNiftModal = useCallback(() => {
    setShowNiftModal(false);
  }, []);

  const onClaimNiftGift = useCallback(() => {
    if (niftAffiliateUrl) {
      window.open(niftAffiliateUrl);
    }
    setShowNiftModal(false);
  }, [niftAffiliateUrl]);

  /* Ensuring browser has finished rendering before we try to scroll to top */
  useEffect(() => {
    const handleScrollToTop = () => {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    };
    const animationFrameId = requestAnimationFrame(handleScrollToTop);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  const handleCopy = useCallback(() => {
    navigator.clipboard
      .writeText(shortLink)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch((error) => {
        evite.error('Failed to copy:', error);
      });
  }, [shortLink]);

  const CopyButton = useMemo(
    () => (
      <Button
        size={isDesktop ? 'large' : 'small'}
        variant={copied ? 'secondary' : 'primary'}
        className="confirm-page__copy-share-btn"
        onClick={handleCopy}
        icon={copied ? <CheckmarkIcon ratio={0.5} /> : null}
        data-qa-id="confirm-page-copy-link-btn"
      >
        {copied ? 'Copied!' : 'Copy link'}
      </Button>
    ),
    [copied, isDesktop, handleCopy]
  );

  return (
    <div className="sign-up-sheets__confirm-page">
      <div className="confirm-page__header">
        <Typography variant={isDesktop ? 'header3' : 'header4'} as="h1">
          Congratulations!
        </Typography>
        <Typography className="confirm-page__subtitle" variant="paragraph2" color="pebble">
          Your sign up sheet has been created.
          <Link data-qa-id="confirm-page-view-sheet-link" href={`/signup-sheet/${sheetId}`}>
            View sheet
          </Link>
        </Typography>
      </div>
      <div className="confirm-page__section">
        <Typography variant={isDesktop ? 'header4' : 'header5'} as="h2">
          Share your sheet
        </Typography>
        <div className="confirm-page__copy-share-url">
          {shortLink ? (
            <Typography
              className="confirm-page__copy-share-text"
              variant="paragraph1"
              data-qa-id="sharable-link"
            >
              {shortLink}
            </Typography>
          ) : null}
          {isDesktop ? CopyButton : null}
        </div>
        {!isDesktop ? CopyButton : null}
        <div className="confirm-page__social-links">
          <LinkIcon color={coin} ratio={0.9} />
          <MailIcon color={coin} ratio={0.675} />
          <SocialWaIcon color={coin} ratio={0.9} />
          <SocialFbIcon color={coin} ratio={0.7} />
          <SocialIgIcon color={coin} ratio={0.7} />
        </div>
      </div>

      {hasDefaultWishlist && (
        <div className="confirm-page__section">
          <Typography variant={isDesktop ? 'header4' : 'header5'} as="h2">
            Add your wishlist
          </Typography>
          <Typography variant="paragraph2" color="pebble">
            Add a wishlist from any retailer
          </Typography>
          <Video
            className="confirm-page__wishlist-url-anim"
            src="https://g0.evitecdn.com/signup-sheets/success-page-wishlist-url-animation.mp4"
          />
          <VendorLogos noGap />
          <Link
            variant={isDesktop ? 'large' : 'small'}
            as="secondary"
            data-qa-id="confirm-page-add-wishlist-btn"
            href={`/signup-sheet-maker/${sheetId}/edit#signup-sheet__wishlist-section`}
          >
            Add now
          </Link>
        </div>
      )}
      <ResponsiveModal
        onClose={onCloseNiftModal}
        isOpen={showNiftModal && niftGiftAvailable}
        title={
          <span className="confirm-page__nift-title">
            Creating this SignUp Sheet just earned you a $30 gift!
          </span>
        }
        primaryText="Claim your gift"
        primaryAction={onClaimNiftGift}
        showSecondary={false}
        body={
          <div className="confirm-page__nift-body">
            <Typography as="span" variant="paragraph1">
              {`Thanks for using Evite${firstName && `, ${firstName}`}. Enjoy a $30 gift card on us. Use it to try
              something new — wine, music, food delivery and more.`}
            </Typography>
            <img
              className="host-success__img"
              src="https://g0.evitecdn.com/ads/ads/end_horn.gif"
              height="120"
              width="120"
              alt=""
            />
          </div>
        }
      />
    </div>
  );
};
